import React from "react";
import ReactDOM from "react-dom/client";
import _ from "lodash";
import RpaPlanningResource from "./RpaPlanningResource";
import RpaPlanningSideSearch from "./RpaPlanningSideSearch";
import RpaPlanningHeaderSearch from "./RpaPlanningHeaderSearch";

/** @typedef {import('../models/RpaResourceModel.js').RpaResourceItem } RpaResourceItem */
/** @typedef {import('../models/RpaTransportModel.js').RpaTransportItem } RpaTransportItem */
/** @typedef {import('../models/RpaActivityModel.js').RpaActivityItem } RpaActivityItem */
/** @typedef {import('../../../app/models/UserModel.js').UserItem } UserItem */
/** @typedef {import('../../../app/models/CloseDayModel.js').CloseDayItem } CloseDayItem */
/** @typedef {import('../services/RpaPlanningService.js').RpaResourcesPerDayObj } RpaResourcesPerDayObj */
/** @typedef {import('../controllers/RpaController.js').FilterObj } FilterObj */
/** @typedef {import('../controllers/RpaController.js').RpaResourceInFilterObj } RpaResourceInFilterObj */

/**
 * @typedef {object} RpaPlanningProps
 * @property {FilterObj} filter - Filtre
 * @property {'front'|'back'} view - Vue du planning
 * @property {?UserItem} connectedUser - Utilisateur connecté
 * @property {RpaOptionsObj} optionsObj - Options RPA
 * @property {number} columnWidth - Largeur des colonnes des resources
 * @property {Array<RpaResourceItem>} rpaResources - Toutes les ressources
 * @property {Array<CloseDayItem>} closeDays - Jours de fermeture
 * @property {?string} originalUrl - Url original
 * @property {'day'|'week'} displayMode - Mode d'affichage (jour ou semaine)
 * @property {string} returnUrl - URL de retour dans le planning si on vient d'une autre page
 * @property {string} lang - Langue Gestiaweb
 * @property {string} source - Source PWA ou pas
 * @property {?string} csrf - Token CSRF pour les requêtes API
 * @property {Array<RpaResourcesPerDayObj>} rpaResourcesPerDays - Ressources par jour
 * @property {Array<RpaResourcesPerDayObj>} filteredRpaResourcesPerDays - Ressources filtrées par jour
 */

/**
 * @typedef {object} RpaOptionsObj
 * @property {boolean} transportAndParkingOption - Gestion des transports et stationnements
 * @property {boolean} cellheightWithGridScheduleOption - Option de hauteur de cellule en fonction des durées de créneau
 * @property {boolean} showScheduleEndHourOption - Option d'affichage de l'heure de fin
 * @property {boolean} showAvailableReservationsOption - Option d'affichage des réservations disponibles sur une case
 * @property {boolean} showFirstReservationOnCaseOption - Option d'affichage de la première réservation sur une case
 * @property {boolean} showNbTotalPersonsOption - Option d'affichage du nombre total de personnes
 * @property {string} frontPlanningUrl - Url du planning front-office
 * @property {'day'|'week'} displayMode - Mode d'affichage (jour ou semaine)
 */

class RpaPlanning extends React.Component {
  /**
   * Constructeur du composant RpaPlanning
   * @param {any} props - Propriétés du composant
   */
  constructor(props) {
    super(props);

    // @ts-ignore
    moment.locale("fr");

    this.state = /** @type {RpaPlanningProps}*/ ({
      source: props.source === "" ? null : props.source,
      view: props.view,
      lang: props.lang === "" ? null : props.lang,
      filter: {
        dayDate: new Date(),
        dayDateStr: "",
        dayInWeekStr: "",
        dateTitle: "",
        rpaActivityId: null,
        rpaResources: []
      },
      returnUrl: props.returnUrl || null,
      optionsObj: /** @type {RpaOptionsObj}*/ ({}),
      connectedUser: null,
      columnWidth: 350,
      rpaResources: [],
      displayMode: "day",
      csrf: null,
      rpaResourcesPerDays: [],
      filteredRpaResourcesPerDays: [],
      rpaActivities: [],
      currentRpaActivity: null,
      closeDays: [],
      originalUrl: null
    });
  }

  async componentDidMount() {
    var dayDateStr = this.props.dayDateStr,
      rpaActivityId = this.props.rpaActivityId === "null" ? null : parseInt(this.props.rpaActivityId, 10),
      optionsObj = await this.fecthOptions(),
      rpaResourcesFilter = JSON.parse(/** @type {string} */ (localStorage.getItem("rpaResourcesFilter"))),
      rpaDisplayMode = JSON.parse(/** @type {string} */ (localStorage.getItem("rpaDisplayMode"))),
      rpaColumnWidth = JSON.parse(/** @type {string} */ (localStorage.getItem("rpaColumnWidth"))),
      displayMode = this.state.view === "back" ? rpaDisplayMode || this.state.displayMode : optionsObj.displayMode,
      planningObj = await this.fetchPlanningObj(dayDateStr, displayMode),
      filterObj = await this.fetchFilterObj(true, dayDateStr, rpaActivityId);

    if (rpaResourcesFilter) {
      filterObj.rpaResources = rpaResourcesFilter;
    }

    return this.setState(
      {
        optionsObj,
        columnWidth: rpaColumnWidth || this.state.columnWidth,
        displayMode,
        rpaResources: planningObj.rpaResources,
        connectedUser: planningObj.connectedUser,
        rpaResourcesPerDays: planningObj.rpaResourcesPerDays,
        filteredRpaResourcesPerDays: planningObj.rpaResourcesPerDays,
        filter: filterObj,
        originalUrl: planningObj.originalUrl,
        closeDays: planningObj.closeDays,
        csrf: planningObj.csrf
      },
      () => {
        this.refreshRpaActiveResources();
      }
    );
  }

  /**
   * Permet de récupérer les options RPA via l'API
   * @returns {Promise<RpaOptionsObj>} - Options RPA
   */
  async fecthOptions() {
    var apiUrl = "/api/rpa/search/options",
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      });

    return await response.json();
  }

  /**
   * Permet de récupérer l'objet du planning RPA via l'API
   * @param {string} dayDateStr - Date au format string pour afficher le planning
   * @param {'day'|'week'} displayMode - Mode d'affichage (jour ou semaine)
   * @returns {Promise<{rpaResourcesPerDays: Array<RpaResourcesPerDayObj>, rpaResources: Array<RpaResourceItem>, rpaActivities: Array<RpaActivityItem>, date: Date, connectedUser: UserItem, originalUrl: string, closeDays: Array<CloseDayItem>, csrf: string}>} - Objet du planning RPA
   */
  async fetchPlanningObj(dayDateStr, displayMode) {
    var apiUrl = `/api/rpa/search/planning?dayDateStr=${dayDateStr}&view=${this.state.view}&displayMode=${displayMode}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      });

    return await response.json();
  }

  /**
   * Permet de récupérer l'objet des filtres de recherche sur le planning via l'API
   * @param {boolean} loadRpaResources - Indique si on doit charger les ressources RPA
   * @param {string} dayDateStr - Date courante au format dd/MM/yyyy
   * @param {?number} rpaActivityId - Id de l'activité
   * @returns {Promise<FilterObj>} - Objet des filtres de recherche sur le planning
   */
  async fetchFilterObj(loadRpaResources, dayDateStr, rpaActivityId) {
    var apiUrl = `/api/rpa/search/filter?loadRpaResources=${loadRpaResources}&dayDateStr=${dayDateStr}${rpaActivityId ? `&rpaActivityId=${rpaActivityId}` : ""}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      });

    return await response.json();
  }

  /**
   * Permet de rafraichir les données du planning en appelant l'api et en mettant à jour le state
   * @param {string} dayDateStr - Date au format string pour afficher le planning
   * @returns {Promise<void>} - Promise une fois le state mis à jour
   */
  async refreshDatas(dayDateStr) {
    var planningObj = await this.fetchPlanningObj(dayDateStr, this.state.displayMode),
      filterObj = await this.fetchFilterObj(false, dayDateStr, this.state.filter.rpaActivityId);

    return new Promise(resolve => {
      this.setState(
        {
          filter: { ...this.state.filter, ...filterObj },
          rpaResourcesPerDays: planningObj.rpaResourcesPerDays,
          filteredRpaResourcesPerDays: planningObj.rpaResourcesPerDays
        },
        resolve
      );
    });
  }

  /**
   * Rafraichissement des ressources actives
   */
  refreshRpaActiveResources() {
    var view = this.state.view,
      filter = this.state.filter,
      rpaResourcesPerDays = this.state.rpaResourcesPerDays,
      filteredRpaResourcesPerDays = rpaResourcesPerDays.map(filteredRpaResourcesPerDay => {
        var filteredRpaResources = /** @type {Array<RpaResourceInFilterObj>} */ (/** @type {FilterObj} */ (filter).rpaResources).reduce((array, rpaResourceInFilter) => {
          let findedRpaResource = filteredRpaResourcesPerDay.rpaResources.find(rpaResource => rpaResource.id === rpaResourceInFilter.rpaResourceId);

          if (filter.rpaActivityId) {
            findedRpaResource = filteredRpaResourcesPerDay.rpaResources.find(rpaResource => rpaResource.rpaActivityId === this.state.filter.rpaActivityId && rpaResource.id === rpaResourceInFilter.rpaResourceId);
          }

          if (findedRpaResource) {
            if (view === "back" && rpaResourceInFilter.checked) {
              array.push(findedRpaResource);
            } else if (view === "front") {
              array.push(findedRpaResource);
            }
          }

          return array;
        }, /** @type {Array<RpaResourceItem>}*/ ([]));

        return {
          rpaResources: filteredRpaResources,
          dayStr: filteredRpaResourcesPerDay.dayStr,
          dayWeekStr: filteredRpaResourcesPerDay.dayWeekStr
        };
      });

    this.setState({
      filteredRpaResourcesPerDays
    });
  }

  /**
   * Permet d'obtenir le style d'une colonne des resources en fonction du nombre de ressources
   * @param {number} nbRpaResources - Nombre de resource
   * @returns {object}
   */
  _getResaPlanStyle(nbRpaResources) {
    return this.state.view === "front"
      ? {
          gridTemplateColumns: `repeat(${nbRpaResources || 1}, minmax(120px, 1fr))`
        }
      : {
          gridTemplateColumns: `repeat(${nbRpaResources || 1}, ${this.state.columnWidth}px)`
        };
  }

  /* ----------------------- Événements ----------------------- */
  /**
   * Changement de la largeur des colonnes
   * @param {PointerEvent} e - Evenement de changement de la largeur des colonnes
   */
  onColumnWidthChange(e) {
    var value = /** @type {HTMLInputElement}*/ (e.target).value;
    localStorage.setItem("rpaColumnWidth", JSON.stringify(value));
    this.setState({
      columnWidth: value
    });
  }

  /**
   * Changement de la date
   * @param {string} dateValue - Date formattée en string (DD/MM/YYYY)
   * @param {object} inputRef - Référence de l'input sur la barre de recherche horizontale
   */
  onDateChange(dateValue, inputRef) {
    this.refreshDatas(dateValue).then(() => {
      this.refreshRpaActiveResources();
      /** @type {any}*/ (inputRef).current.value = dateValue;
    });
  }

  /**
   * Activation ou désactivation d'une ressource
   * @param {number} rpaResourceId - Identifiant de la ressource
   * @param {PointerEvent} e - Evenement de changement de la checkbox
   */
  onChangeOnRpaResource(rpaResourceId, e) {
    var target = /** @type {HTMLInputElement}*/ (e.target),
      formattedRpaResourceInFilter = /** @type {FilterObj}*/ (this.state.filter).rpaResources.map(rpaResourceInFilter => {
        return rpaResourceInFilter.rpaResourceId === rpaResourceId
          ? {
              rpaResourceId: rpaResourceInFilter.rpaResourceId,
              obj: rpaResourceInFilter.obj,
              color: rpaResourceInFilter.color,
              name: rpaResourceInFilter.name,
              checked: target.checked
            }
          : rpaResourceInFilter;
      });

    this.setState(
      {
        filter: { ...this.state.filter, ...{ rpaResources: formattedRpaResourceInFilter } }
      },
      () => {
        this.refreshRpaActiveResources();
        localStorage.setItem("rpaResourcesFilter", JSON.stringify(this.state.filter.rpaResources));
      }
    );
  }

  /**
   * Lors du changement du mode d'affichage (semaine ou journée), on rappel l'api avec le bon mode pour mettre à jour le planning
   * @param {PointerEvent} e - événement associé au changement de valeur dans le select
   */
  onChangeOnDisplayMode(e) {
    var target = /** @type {HTMLSelectElement}*/ (e.target),
      displayMode = target.value;

    this.setState(
      {
        displayMode
      },
      () => {
        localStorage.setItem("rpaDisplayMode", JSON.stringify(displayMode));
        this.refreshDatas(this.state.filter.dayDateStr).then(() => {
          this.refreshRpaActiveResources();
        });
      }
    );
  }
  /* ----------------------- /Événements ----------------------- */

  render() {
    /** @type {React.CSSProperties} */
    var mainStyle = {
      height: "100%"
    };

    if (this.state.view === "front") {
      mainStyle.padding = "16px";
    }

    /** @type {React.CSSProperties} */
    var resaPlanGridStyle = {
      gridTemplateColumns: "auto 1fr",
      gap: "8px"
    };

    /** @type {React.CSSProperties} */
    var resaPlanContainerStyle = {
      display: "grid",
      gridTemplateColumns: `repeat(${this.state.rpaResourcesPerDays.length}, 1fr)`,
      alignItems: "flex-start"
    };

    return (
      <div style={mainStyle}>
        <div style={{ height: "100%" }}>
          {this.state.view === "back" ? (
            <div className="resaplan-grid" style={resaPlanGridStyle}>
              <RpaPlanningSideSearch filter={this.state.filter} columnWidth={this.state.columnWidth} view={this.state.view} transportAndParkingOption={this.state.optionsObj.transportAndParkingOption} onColumnWidthChange={this.onColumnWidthChange.bind(this)} onDateChange={this.onDateChange.bind(this)} onChangeOnRpaResource={this.onChangeOnRpaResource.bind(this)} onChangeOnDisplayMode={this.onChangeOnDisplayMode.bind(this)} displayMode={this.state.displayMode} connectedUser={this.state.connectedUser} />
              <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: "8px", height: "100%", overflowY: "auto" }}>
                <RpaPlanningHeaderSearch transportAndParkingOption={this.state.optionsObj.transportAndParkingOption} view={this.state.view} filter={this.state.filter} onDateChange={this.onDateChange.bind(this)} />
                <div className="resaplan--container" style={resaPlanContainerStyle}>
                  {this.state.filteredRpaResourcesPerDays.map((filteredRpaResourcesPerDay, index) => (
                    <div key={index} className={filteredRpaResourcesPerDay.dayStr === this.state.filter.dayDateStr ? "resaplan__day-column resaplan__day-column--current" : "resaplan__day-column"}>
                      <p className="resaplan__day-column-title">
                        <span className="resaplan__day-column-title-day">{filteredRpaResourcesPerDay.dayWeekStr}</span>
                        <span className="resaplan__day-column-title-date">{filteredRpaResourcesPerDay.dayStr}</span>
                      </p>
                      <div className="resaplan" style={this._getResaPlanStyle(filteredRpaResourcesPerDay.rpaResources.length)}>
                        {filteredRpaResourcesPerDay.rpaResources.map(rpaResource => (
                          <RpaPlanningResource key={rpaResource.id} rpaResource={rpaResource} transportAndParkingOption={this.state.optionsObj.transportAndParkingOption} filter={this.state.filter} view={this.state.view} cellheightWithGridScheduleOption={this.state.optionsObj.cellheightWithGridScheduleOption} closeDays={this.state.closeDays} showScheduleEndHourOption={this.state.optionsObj.showScheduleEndHourOption} showAvailableReservationsOption={this.state.optionsObj.showAvailableReservationsOption} showFirstReservationOnCaseOption={this.state.optionsObj.showFirstReservationOnCaseOption} showNbTotalPersonsOption={this.state.optionsObj.showNbTotalPersonsOption} connectedUser={this.state.connectedUser} lang={this.state.lang} source={this.state.source} dayStr={filteredRpaResourcesPerDay.dayStr} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="resaplan-grid" style={resaPlanGridStyle}>
              <RpaPlanningSideSearch filter={this.state.filter} columnWidth={this.state.columnWidth} view={this.state.view} transportAndParkingOption={this.state.optionsObj.transportAndParkingOption} onColumnWidthChange={this.onColumnWidthChange.bind(this)} onDateChange={this.onDateChange.bind(this)} onChangeOnRpaResource={this.onChangeOnRpaResource.bind(this)} onChangeOnDisplayMode={this.onChangeOnDisplayMode.bind(this)} displayMode={this.state.displayMode} connectedUser={this.state.connectedUser} />
              <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: "8px", height: "100%", overflowY: "auto" }}>
                <RpaPlanningHeaderSearch transportAndParkingOption={this.state.optionsObj.transportAndParkingOption} view={this.state.view} filter={this.state.filter} onDateChange={this.onDateChange.bind(this)} returnUrl={this.state.returnUrl} />
                <div className="resaplan--container" style={resaPlanContainerStyle}>
                  {this.state.filteredRpaResourcesPerDays.map((filteredRpaResourcesPerDay, index) => (
                    <div key={index} className={filteredRpaResourcesPerDay.dayStr === this.state.filter.dayDateStr ? "resaplan__day-column resaplan__day-column--current" : "resaplan__day-column"}>
                      <p className="resaplan__day-column-title">
                        <span className="resaplan__day-column-title-day">{filteredRpaResourcesPerDay.dayWeekStr}</span>
                        <span className="resaplan__day-column-title-date">{filteredRpaResourcesPerDay.dayStr}</span>
                      </p>
                      <div className="resaplan" style={this._getResaPlanStyle(filteredRpaResourcesPerDay.rpaResources.length)}>
                        {filteredRpaResourcesPerDay.rpaResources.map(rpaResource => (
                          <RpaPlanningResource key={rpaResource.id} rpaResource={rpaResource} transportAndParkingOption={this.state.optionsObj.transportAndParkingOption} filter={this.state.filter} view={this.state.view} cellheightWithGridScheduleOption={this.state.optionsObj.cellheightWithGridScheduleOption} closeDays={this.state.closeDays} showScheduleEndHourOption={this.state.optionsObj.showScheduleEndHourOption} showAvailableReservationsOption={this.state.optionsObj.showAvailableReservationsOption} showFirstReservationOnCaseOption={this.state.optionsObj.showFirstReservationOnCaseOption} showNbTotalPersonsOption={this.state.optionsObj.showNbTotalPersonsOption} connectedUser={this.state.connectedUser} lang={this.state.lang} source={this.state.source} dayStr={filteredRpaResourcesPerDay.dayStr} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

var rpaPlanningNode = document.getElementById("rpa-planning");
if (rpaPlanningNode) {
  var root = ReactDOM.createRoot(rpaPlanningNode);
  root.render(<RpaPlanning dayDateStr={rpaPlanningNode.getAttribute("data-daydatestr")} view={rpaPlanningNode.getAttribute("data-view")} rpaActivityId={rpaPlanningNode.getAttribute("data-rpaactivityid")} source={rpaPlanningNode.getAttribute("data-source")} lang={rpaPlanningNode.getAttribute("data-lang")} returnUrl={rpaPlanningNode.getAttribute("data-returnurl")} />);
}
